import React, { useState } from 'react';
import { BarChartIcon, TrendingUpIcon, ShoppingCartIcon, UsersIcon, PackageIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon, InfoIcon } from 'lucide-react';
import { Shop } from '../pages/Shops';
interface ShopDashboardProps {
  shop: Shop;
}
export function ShopDashboard({
  shop
}: ShopDashboardProps) {
  const [hoveredRevenue, setHoveredRevenue] = useState<{
    month: string;
    revenue: number;
  } | null>(null);
  const [hoveredOrder, setHoveredOrder] = useState<{
    month: string;
    orders: number;
  } | null>(null);
  // Create more pronounced seasonal pattern for a beach shop
  // Higher sales in summer months, peak in July-August, lower in winter
  const seasonalPattern = [0.25, 0.3, 0.45, 0.6, 0.75, 0.9, 1.0, 0.95, 0.7, 0.5, 0.35, 0.4];
  // Generate revenue data with more pronounced seasonal patterns
  const revenueData = [{
    month: 'Jan',
    revenue: Math.round(shop.revenue.total * seasonalPattern[0])
  }, {
    month: 'Feb',
    revenue: Math.round(shop.revenue.total * seasonalPattern[1])
  }, {
    month: 'Mar',
    revenue: Math.round(shop.revenue.total * seasonalPattern[2])
  }, {
    month: 'Apr',
    revenue: Math.round(shop.revenue.total * seasonalPattern[3])
  }, {
    month: 'May',
    revenue: Math.round(shop.revenue.total * seasonalPattern[4])
  }, {
    month: 'Jun',
    revenue: Math.round(shop.revenue.total * seasonalPattern[5])
  }, {
    month: 'Jul',
    revenue: Math.round(shop.revenue.total * seasonalPattern[6])
  }, {
    month: 'Aug',
    revenue: Math.round(shop.revenue.total * seasonalPattern[7])
  }, {
    month: 'Sep',
    revenue: Math.round(shop.revenue.total * seasonalPattern[8])
  }, {
    month: 'Oct',
    revenue: Math.round(shop.revenue.total * seasonalPattern[9])
  }, {
    month: 'Nov',
    revenue: Math.round(shop.revenue.total * seasonalPattern[10])
  }, {
    month: 'Dec',
    revenue: Math.round(shop.revenue.total * seasonalPattern[11])
  }];
  // Generate orders data with the same seasonal pattern
  const ordersData = [{
    month: 'Jan',
    orders: Math.round(shop.ordersCount * seasonalPattern[0])
  }, {
    month: 'Feb',
    orders: Math.round(shop.ordersCount * seasonalPattern[1])
  }, {
    month: 'Mar',
    orders: Math.round(shop.ordersCount * seasonalPattern[2])
  }, {
    month: 'Apr',
    orders: Math.round(shop.ordersCount * seasonalPattern[3])
  }, {
    month: 'May',
    orders: Math.round(shop.ordersCount * seasonalPattern[4])
  }, {
    month: 'Jun',
    orders: Math.round(shop.ordersCount * seasonalPattern[5])
  }, {
    month: 'Jul',
    orders: Math.round(shop.ordersCount * seasonalPattern[6])
  }, {
    month: 'Aug',
    orders: Math.round(shop.ordersCount * seasonalPattern[7])
  }, {
    month: 'Sep',
    orders: Math.round(shop.ordersCount * seasonalPattern[8])
  }, {
    month: 'Oct',
    orders: Math.round(shop.ordersCount * seasonalPattern[9])
  }, {
    month: 'Nov',
    orders: Math.round(shop.ordersCount * seasonalPattern[10])
  }, {
    month: 'Dec',
    orders: Math.round(shop.ordersCount * seasonalPattern[11])
  }];
  const topProducts = [{
    name: 'iPhone 15 Pro',
    sales: 38,
    revenue: 37999.62
  }, {
    name: 'Samsung Galaxy S24',
    sales: 27,
    revenue: 22949.73
  }, {
    name: 'Apple Watch Ultra 2',
    sales: 24,
    revenue: 19199.76
  }, {
    name: 'iPad Pro 12.9"',
    sales: 22,
    revenue: 24199.78
  }];
  const customerStats = {
    total: 287,
    new: 42,
    returning: 245,
    conversionRate: 3.8
  };
  // Calculate max values for chart scaling
  const maxRevenue = Math.max(...revenueData.map(item => item.revenue));
  const maxOrders = Math.max(...ordersData.map(item => item.orders));
  return <div className="space-y-6" data-id="element-3422">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3423">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-3424">
          <BarChartIcon size={18} className="mr-2 text-blue-600" data-id="element-3425" />
          Shop Dashboard
        </h2>
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-3426">
          {/* Revenue KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3427">
            <div className="flex justify-between items-start" data-id="element-3428">
              <div data-id="element-3429">
                <p className="text-sm text-gray-500 mb-1" data-id="element-3430">Total Revenue</p>
                <div className="text-2xl font-bold text-gray-900" data-id="element-3431">MYR</div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-3432">
                  {shop.revenue.total.toLocaleString('en-MY')}
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-md" data-id="element-3433">
                <DollarSignIcon size={20} className="text-blue-600" data-id="element-3434" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-3435">
              <span className={`inline-flex items-center text-xs font-medium ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-3436">
                {shop.revenue.growth >= 0 ? <ArrowUpIcon size={12} className="mr-1" data-id="element-3437" /> : <ArrowDownIcon size={12} className="mr-1" data-id="element-3438" />}
                {Math.abs(shop.revenue.growth)}%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-3439">vs last month</span>
            </div>
          </div>
          {/* Orders KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3440">
            <div className="flex justify-between items-start" data-id="element-3441">
              <div data-id="element-3442">
                <p className="text-sm text-gray-500 mb-1" data-id="element-3443">Total Orders</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-3444">
                  {shop.ordersCount}
                </h3>
              </div>
              <div className="p-2 bg-purple-50 rounded-md" data-id="element-3445">
                <ShoppingCartIcon size={20} className="text-purple-600" data-id="element-3446" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-3447">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-3448">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-3449" />
                12.4%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-3450">vs last month</span>
            </div>
          </div>
          {/* Products KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3451">
            <div className="flex justify-between items-start" data-id="element-3452">
              <div data-id="element-3453">
                <p className="text-sm text-gray-500 mb-1" data-id="element-3454">Total Products</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-3455">
                  {shop.productsCount}
                </h3>
              </div>
              <div className="p-2 bg-green-50 rounded-md" data-id="element-3456">
                <PackageIcon size={20} className="text-green-600" data-id="element-3457" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-3458">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-3459">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-3460" />
                5.2%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-3461">vs last month</span>
            </div>
          </div>
          {/* Customers KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3462">
            <div className="flex justify-between items-start" data-id="element-3463">
              <div data-id="element-3464">
                <p className="text-sm text-gray-500 mb-1" data-id="element-3465">Total Customers</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-3466">
                  {customerStats.total}
                </h3>
              </div>
              <div className="p-2 bg-orange-50 rounded-md" data-id="element-3467">
                <UsersIcon size={20} className="text-orange-600" data-id="element-3468" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-3469">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-3470">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-3471" />
                8.7%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-3472">vs last month</span>
            </div>
          </div>
        </div>
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-3473">
          {/* Revenue Chart - Completely revamped implementation */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3474">
            <div className="flex justify-between items-center mb-4" data-id="element-3475">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-3476">
                Revenue Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-3477">
                <CalendarIcon size={14} className="mr-1" data-id="element-3478" />
                Last 12 months
              </div>
            </div>
            <div className="relative h-60 mb-6" data-id="element-3479">
              {/* Tooltip */}
              {hoveredRevenue && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10" data-id="element-3480">
                  {hoveredRevenue.month}: MYR{' '}
                  {hoveredRevenue.revenue.toLocaleString('en-MY')}
                </div>}
              {/* Y-axis labels */}
              <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-3481">
                <div data-id="element-3482">MYR {maxRevenue.toLocaleString('en-MY')}</div>
                <div data-id="element-3483">
                  MYR {Math.round(maxRevenue * 0.75).toLocaleString('en-MY')}
                </div>
                <div data-id="element-3484">
                  MYR {Math.round(maxRevenue * 0.5).toLocaleString('en-MY')}
                </div>
                <div data-id="element-3485">
                  MYR {Math.round(maxRevenue * 0.25).toLocaleString('en-MY')}
                </div>
                <div data-id="element-3486">MYR 0</div>
              </div>
              {/* Chart area */}
              <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-3487">
                {/* Horizontal grid lines */}
                <div className="absolute inset-0" data-id="element-3488">
                  <div className="border-t border-gray-100 h-1/4" data-id="element-3489"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-3490"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-3491"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-3492"></div>
                </div>
                {/* Chart bars */}
                <div className="relative w-full h-full flex items-end" data-id="element-3493">
                  {revenueData.map((item, index) => {
                  const barHeight = `${item.revenue / maxRevenue * 100}%`;
                  return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredRevenue(item)} onMouseLeave={() => setHoveredRevenue(null)} data-id="element-3494">
                        <div style={{
                      height: barHeight
                    }} className={`w-4/5 ${index === 11 ? 'bg-blue-600' : 'bg-blue-500 hover:bg-blue-600'} rounded-t transition-all duration-200`} data-id="element-3495"></div>
                      </div>;
                })}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-500 pt-2" data-id="element-3496">
                {revenueData.map((item, index) => <div key={index} className="text-center" data-id="element-3497">
                    {item.month}
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center" data-id="element-3498">
              <span className="inline-flex items-center" data-id="element-3499">
                <InfoIcon size={12} className="mr-1" data-id="element-3500" />
                Electronics shop revenue shows consistent growth trend
              </span>
            </div>
          </div>
          {/* Orders Chart - Completely revamped implementation */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3501">
            <div className="flex justify-between items-center mb-4" data-id="element-3502">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-3503">
                Orders Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-3504">
                <CalendarIcon size={14} className="mr-1" data-id="element-3505" />
                Last 12 months
              </div>
            </div>
            <div className="relative h-60 mb-6" data-id="element-3506">
              {/* Tooltip */}
              {hoveredOrder && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10" data-id="element-3507">
                  {hoveredOrder.month}: {hoveredOrder.orders.toLocaleString()}{' '}
                  orders
                </div>}
              {/* Y-axis labels */}
              <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-3508">
                <div data-id="element-3509">{maxOrders.toLocaleString()}</div>
                <div data-id="element-3510">{Math.round(maxOrders * 0.75).toLocaleString()}</div>
                <div data-id="element-3511">{Math.round(maxOrders * 0.5).toLocaleString()}</div>
                <div data-id="element-3512">{Math.round(maxOrders * 0.25).toLocaleString()}</div>
                <div data-id="element-3513">0</div>
              </div>
              {/* Chart area */}
              <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-3514">
                {/* Horizontal grid lines */}
                <div className="absolute inset-0" data-id="element-3515">
                  <div className="border-t border-gray-100 h-1/4" data-id="element-3516"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-3517"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-3518"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-3519"></div>
                </div>
                {/* Chart bars */}
                <div className="relative w-full h-full flex items-end" data-id="element-3520">
                  {ordersData.map((item, index) => {
                  const barHeight = `${item.orders / maxOrders * 100}%`;
                  return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredOrder(item)} onMouseLeave={() => setHoveredOrder(null)} data-id="element-3521">
                        <div style={{
                      height: barHeight
                    }} className={`w-4/5 ${index === 11 ? 'bg-purple-600' : 'bg-purple-500 hover:bg-purple-600'} rounded-t transition-all duration-200`} data-id="element-3522"></div>
                      </div>;
                })}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-500 pt-2" data-id="element-3523">
                {ordersData.map((item, index) => <div key={index} className="text-center" data-id="element-3524">
                    {item.month}
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center" data-id="element-3525">
              <span className="inline-flex items-center" data-id="element-3526">
                <InfoIcon size={12} className="mr-1" data-id="element-3527" />
                Order volume shows steady growth in electronics sales
              </span>
            </div>
          </div>
        </div>
        {/* Additional Insights Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3528">
          {/* Top Products */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3529">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3530">
              Top Selling Products
            </h4>
            <div className="space-y-3" data-id="element-3531">
              {topProducts.map((product, index) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-3532">
                  <div className="flex items-center" data-id="element-3533">
                    <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center text-gray-500 mr-3" data-id="element-3534">
                      {index + 1}
                    </div>
                    <div data-id="element-3535">
                      <p className="text-sm font-medium text-gray-800" data-id="element-3536">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-3537">
                        {product.sales} sold
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-3538">
                    <p className="text-sm font-medium text-gray-800" data-id="element-3539">
                      MYR{' '}
                      {product.revenue.toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-3540">Revenue</p>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Customer Insights */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-3541">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3542">
              Customer Insights
            </h4>
            <div className="grid grid-cols-2 gap-4" data-id="element-3543">
              <div className="bg-gray-50 rounded-md p-3" data-id="element-3544">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3545">New Customers</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-3546">
                  {customerStats.new}
                </p>
                <div className="mt-1 flex items-center" data-id="element-3547">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-3548">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-3549" />
                    14.3%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-3550">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3551">
                  Returning Customers
                </p>
                <p className="text-lg font-medium text-gray-800" data-id="element-3552">
                  {customerStats.returning}
                </p>
                <div className="mt-1 flex items-center" data-id="element-3553">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-3554">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-3555" />
                    7.2%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-3556">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3557">Avg. Order Value</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-3558">
                  MYR{' '}
                  {(shop.revenue.total / shop.ordersCount).toLocaleString('en-MY', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </p>
                <div className="mt-1 flex items-center" data-id="element-3559">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-3560">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-3561" />
                    3.5%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-3562">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3563">Conversion Rate</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-3564">
                  {customerStats.conversionRate}%
                </p>
                <div className="mt-1 flex items-center" data-id="element-3565">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-3566">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-3567" />
                    1.2%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}